// src/Components/modules/Coordinacion/HistorialTrasladoDialog.js
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Modal, Typography, Card, Tag, Progress, Badge, Empty } from "antd";
import {
  CarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UserOutlined,

  LoadingOutlined,
  ExclamationCircleOutlined,
  StopOutlined,
  PlayCircleOutlined,
  SyncOutlined,
  SendOutlined,
  EyeOutlined,
  FlagOutlined,
} from "@ant-design/icons";
import api from "../../../axiosConfig";
import { esActorSistema, descripcionHistorial } from "../../../utils/historialSistema";
import "./HistorialTrasladoDialog.css";

const { Text, Title } = Typography;

/**
 * Componente HistorialTrasladoDialog para mostrar el historial de un traslado con diseño moderno de proceso flow.
 */
// Texto del registro sin la etiqueta de ubicación ni la descripción del conductor (se muestran aparte)
const textoBaseHistorial = (registro) => {
  let texto = descripcionHistorial(registro);
  if (!registro.etiqueta_ubicacion && !registro.descripcion_conductor) return texto;
  const corte = texto.search(/\sDescripción:/);
  if (corte >= 0) texto = texto.slice(0, corte);
  if (registro.etiqueta_ubicacion) texto = texto.replace(` ${registro.etiqueta_ubicacion}.`, '');
  return texto.trim();
};

function HistorialTrasladoDialog({ open, onClose, trasladoId }) {
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (trasladoId && open) {
      setLoading(true);
      api
        .get(`/traslados/historial/${trasladoId}`)
        .then((response) => {
          // Manejar diferentes tipos de respuesta de la API
          let historialData = [];
          if (Array.isArray(response.data)) {
            historialData = response.data;
          } else if (response.data && typeof response.data === 'object') {
            // Si es un objeto, buscar si hay un array dentro o convertir a array
            if (response.data.historial && Array.isArray(response.data.historial)) {
              historialData = response.data.historial;
            } else if (response.data.data && Array.isArray(response.data.data)) {
              historialData = response.data.data;
            } else {
              // Si es un objeto vacío o no tiene la estructura esperada
              historialData = [];
            }
          }
          
          setHistorial(historialData);
        })
        .catch((error) => {
          console.error("Error al obtener el historial:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [trasladoId, open]);

  // Definir las etapas del proceso con sus iconos y colores
  const processStages = [
    { 
      key: 'programado', 
      label: 'Programado', 
      icon: <SendOutlined />, 
      color: '#6c06b5',
      keywords: ['programó', 'programa', 'programado']
    },
    { 
      key: 'pre-asignado', 
      label: 'Pre-Asignado', 
      icon: <UserOutlined />, 
      color: '#0c27a0',
      keywords: ['pre-asignó', 'pre-asignado']
    },
    { 
      key: 'asignado', 
      label: 'Asignado', 
      icon: <UserOutlined />, 
      color: '#1f7362',
      keywords: ['asignó', 'asignado']
    },
    { 
      key: 'enterado', 
      label: 'Enterado', 
      icon: <EyeOutlined />, 
      color: '#0c5b07',
      keywords: ['enterado', 'visto por conductor', 'traslado visto']
    },
    { 
      key: 'aceptado', 
      label: 'Aceptado', 
      icon: <CheckCircleOutlined />, 
      color: '#088A08',
      keywords: ['aceptado']
    },
    { 
      key: 'en_camino', 
      label: 'En Camino', 
      icon: <CarOutlined />, 
      color: '#FFBF00',
      keywords: ['en camino']
    },
    { 
      key: 'esperando', 
      label: 'Esperando', 
      icon: <ClockCircleOutlined />, 
      color: '#a44706',
      keywords: ['esperando']
    },
    { 
      key: 'iniciado', 
      label: 'Iniciado', 
      icon: <PlayCircleOutlined />, 
      color: '#fa7114',
      keywords: ['iniciado', 'inició']
    },
    { 
      key: 'finalizado', 
      label: 'Finalizado', 
      icon: <FlagOutlined />, 
      color: '#4d4d4d',
      keywords: ['finalizó', 'finalizado', 'completado']
    },
    { 
      key: 'fallido', 
      label: 'Fallido', 
      icon: <ExclamationCircleOutlined />, 
      color: '#4a2c04',
      keywords: ['fallido']
    },
    { 
      key: 'cancelado', 
      label: 'Cancelado', 
      icon: <StopOutlined />, 
      color: '#b10e1a',
      keywords: ['cancelado', 'canceló']
    },
    { 
      key: 'modificacion', 
      label: 'Modificación', 
      icon: <SyncOutlined />, 
      color: '#722ed1',
      keywords: ['modificación', 'modificó', 'actualizado']
    }
  ];

  const getStageFromAction = (action) => {
    const actionLower = action.toLowerCase();
    
    // Manejar eventos secundarios (no afectan el estado principal)
    if (actionLower.includes('confirmó') || actionLower.includes('validó')) {
      return { key: 'confirmado', label: 'Confirmado', color: '#088A08' };
    }
    if (actionLower.includes('desconfirmó') || actionLower.includes('desvalidó')) {
      return { key: 'desconfirmado', label: 'Desconfirmado', color: '#b10e1a' };
    }
    
    // Buscar en las etapas del proceso principales
    for (const stage of processStages) {
      if (stage.keywords.some(keyword => actionLower.includes(keyword))) {
        return stage;
      }
    }
    
    return { key: 'otros', label: 'Otros', color: '#8c8c8c' };
  };

  const getActionIcon = (action) => {
    const actionLower = action.toLowerCase();
    const iconStyle = { fontSize: '14px' };
    
    if (actionLower.includes('programó')) return <SendOutlined style={iconStyle} />;
    if (actionLower.includes('asignó')) return <UserOutlined style={iconStyle} />;
    if (actionLower.includes('confirmó') || actionLower.includes('validó')) return <CheckCircleOutlined style={iconStyle} />;
    if (actionLower.includes('desconfirmó') || actionLower.includes('desvalidó')) return <CloseCircleOutlined style={iconStyle} />;
    if (actionLower.includes('enterado')) return <EyeOutlined style={iconStyle} />;
    if (actionLower.includes('aceptado')) return <CheckCircleOutlined style={iconStyle} />;
    if (actionLower.includes('en camino')) return <CarOutlined style={iconStyle} />;
    if (actionLower.includes('esperando')) return <ClockCircleOutlined style={iconStyle} />;
    if (actionLower.includes('iniciado') || actionLower.includes('inició')) return <PlayCircleOutlined style={iconStyle} />;
    if (actionLower.includes('finalizó')) return <FlagOutlined style={iconStyle} />;
    if (actionLower.includes('fallido')) return <ExclamationCircleOutlined style={iconStyle} />;
    if (actionLower.includes('cancelado') || actionLower.includes('canceló')) return <StopOutlined style={iconStyle} />;
    
    return <SyncOutlined style={iconStyle} />;
  };

  const formatearAccion = (accionCruda) => {
    switch (accionCruda) {
      case 'Corrección automática de ubicaciones':
        return 'Corrección automática de ubicaciones';
      case 'REINICIO_TRASLADO':
        return 'Reinicio de Traslado';
      case 'ModificacióN':
        return 'Modificación';
      default:
        return accionCruda
          .replace(/_/g, ' ')
          .replace(/\b\w/g, c => c.toUpperCase())
          .replace('ModificacióN', 'Modificación');
    }
  };

  const formatearFecha = (fecha) => {
    const date = new Date(fecha);
    const ahora = new Date();
    const diferencia = ahora - date;
    const minutos = Math.floor(diferencia / 60000);
    const horas = Math.floor(diferencia / 3600000);
    const dias = Math.floor(diferencia / 86400000);

    if (minutos < 1) return "Hace un momento";
    if (minutos < 60) return `Hace ${minutos} min`;
    if (horas < 24) return `Hace ${horas}h`;
    if (dias < 7) return `Hace ${dias}d`;
    return date.toLocaleDateString("es-ES", { 
      day: "numeric", 
      month: "short", 
      year: "numeric" 
    });
  };

  // Calcular el progreso del traslado
  const calculateProgress = () => {
    if (!historial || !Array.isArray(historial) || historial.length === 0) return 0;
    
    // Ordenar historial por fecha para obtener el último evento
    const sortedHistorial = [...historial].sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
    
    // Buscar el último evento que NO sea secundario
    for (const record of sortedHistorial) {
      const actionLower = record.accion.toLowerCase();
      // Excluir eventos secundarios
      if (!actionLower.includes('confirmó') && !actionLower.includes('validó') && 
          !actionLower.includes('desconfirmó') && !actionLower.includes('desvalidó')) {
        
        // Estados finalizados - 100%
        if (actionLower.includes('fallido') || actionLower.includes('canceló') || actionLower.includes('cancelado') || 
            actionLower.includes('finalizó') || actionLower.includes('finalizado')) return 100;
        
        // Estados en curso - progreso incremental
        if (actionLower.includes('iniciado') || actionLower.includes('inició')) return 85;
        if (actionLower.includes('esperando')) return 75;
        if (actionLower.includes('en camino')) return 65;
        if (actionLower.includes('aceptado')) return 55;
        if (actionLower.includes('enterado')) return 45;
        if (actionLower.includes('asignó') || actionLower.includes('asignado')) return 35;
        
        // Estados de programación - progreso inicial
        if (actionLower.includes('pre-asignó') || actionLower.includes('pre-asignado')) return 15;
        if (actionLower.includes('programó') || actionLower.includes('programado')) return 5;
        
        return 0;
      }
    }
    
    return 0;
  };

  const getProgressStatus = () => {
    if (!historial || !Array.isArray(historial) || historial.length === 0) return 'normal';
    
    // Ordenar historial por fecha para obtener el último evento
    const sortedHistorial = [...historial].sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
    
    // Buscar el último evento que NO sea secundario
    for (const record of sortedHistorial) {
      const actionLower = record.accion.toLowerCase();
      // Excluir eventos secundarios
      if (!actionLower.includes('confirmó') && !actionLower.includes('validó') && 
          !actionLower.includes('desconfirmó') && !actionLower.includes('desvalidó')) {
        
        if (actionLower.includes('fallido') || actionLower.includes('canceló') || actionLower.includes('cancelado')) return 'exception';
        if (actionLower.includes('finalizó') || actionLower.includes('finalizado')) return 'success';
        
        return 'active';
      }
    }
    
    return 'normal';
  };

  const getCurrentStage = () => {
    if (!historial || !Array.isArray(historial) || historial.length === 0) return null;
    
    // Ordenar historial por fecha para obtener el último evento
    const sortedHistorial = [...historial].sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
    
    // Buscar el último evento que NO sea secundario
    for (const record of sortedHistorial) {
      const actionLower = record.accion.toLowerCase();
      // Excluir eventos secundarios
      if (!actionLower.includes('confirmó') && !actionLower.includes('validó') && 
          !actionLower.includes('desconfirmó') && !actionLower.includes('desvalidó')) {
        return getStageFromAction(record.accion);
      }
    }
    
    return null;
  };

  const currentStage = getCurrentStage();
  const progress = calculateProgress();
  const progressStatus = getProgressStatus();

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <div className="historial-modal-header">
          <div className="header-icon">
            <CarOutlined />
          </div>
          <div className="header-content">
            <Title level={4} className="header-title">
              Historial del Traslado
            </Title>
            <Text type="secondary" className="header-subtitle">
              Seguimiento del proceso • ID: {trasladoId}
            </Text>
          </div>
          {currentStage && (
            <Badge 
              count={currentStage.label} 
              style={{ 
                backgroundColor: currentStage.color,
                fontSize: '12px',
                fontWeight: 500,
                height: '24px',
                lineHeight: '24px',
                minWidth: '80px'
              }}
            />
          )}
        </div>
      }
      footer={null}
      width={900}
      centered
      className="historial-modal"
      styles={{ 
        body: { padding: 0 }
      }}
    >
      <div className="historial-container">
        {/* Progress Header */}
        <div className="progress-section">
          <div className="progress-info">
            <Text className="progress-label">Progreso del Traslado</Text>
            <Text className="progress-percentage">{progress}%</Text>
          </div>
          <Progress 
            percent={progress} 
            status={progressStatus}
            strokeColor={{
              '0%': '#6c06b5',
              '15%': '#0c27a0',
              '35%': '#1f7362',
              '45%': '#0c5b07',
              '55%': '#088A08',
              '65%': '#FFBF00',
              '75%': '#a44706',
              '85%': '#fa7114',
              '100%': progressStatus === 'exception' ? (progress === 100 && currentStage?.key === 'fallido' ? '#4a2c04' : '#b10e1a') : '#4d4d4d'
            }}
            className="progress-bar"
          />
        </div>


        {/* Timeline Events */}
        <div className="timeline-section">
          <Title level={5} className="timeline-title">
            Eventos del Proceso
          </Title>
          
          {loading ? (
            <div className="loading-state">
              <LoadingOutlined style={{ fontSize: '24px', marginBottom: '16px' }} />
              <Text>Cargando historial...</Text>
            </div>
          ) : (!historial || !Array.isArray(historial) || historial.length === 0) ? (
            <Empty 
              description="No hay eventos en el historial"
              style={{ padding: '40px 0' }}
            />
          ) : (
            <div className="timeline-events">
              {historial && Array.isArray(historial) ? historial.map((registro, index) => {
                const stage = getStageFromAction(registro.accion);
                const isLastEvent = index === historial.length - 1;
                const isCritical = registro.accion.toLowerCase().includes('fallido') || 
                                 registro.accion.toLowerCase().includes('cancelado') ||
                                 registro.accion.toLowerCase().includes('canceló') ||
                                 registro.accion.toLowerCase().includes('desconfirmó') ||
                                 registro.accion.toLowerCase().includes('desvalidó');
                
                return (
                  <div key={index} className={`timeline-event ${isLastEvent ? 'last' : ''} ${isCritical ? 'critical' : ''}`}>
                    <div className="event-connector">
                      <div className="event-dot" style={{ backgroundColor: stage.color }}>
                        {getActionIcon(registro.accion)}
                      </div>
                      {!isLastEvent && <div className="event-line" />}
                    </div>
                    
                    <Card className="event-card">
                      <div className="event-header">
                        <div className="event-actor">
                          <div className="actor-icon">
                            {registro.actor_type === 'conductor' ? (
                              <CarOutlined style={{ color: '#1890ff' }} />
                            ) : (
                              <UserOutlined style={{ color: '#52c41a' }} />
                            )}
                          </div>
                          <div className="actor-info">
                            <Text strong className="actor-name">
                              {esActorSistema(registro) ? 'Sistema' : registro.actor_type === 'conductor'
                                ? (registro.conductor_nombre || `Conductor (ID: ${registro.conductor_id})`)
                                : (registro.usuario_nombre || `Usuario (ID: ${registro.usuario_id})`)}
                            </Text>
                            <Text type="secondary" className="actor-role">
                              {esActorSistema(registro) ? 'Proceso automático' : registro.actor_type === 'conductor' ? 'Conductor' : 'Usuario'}
                            </Text>
                          </div>
                        </div>
                        
                        <div className="event-time">
                          <Text type="secondary" className="time-relative">
                            {formatearFecha(registro.fecha)}
                          </Text>
                          <Text type="secondary" className="time-absolute">
                            {new Date(registro.fecha).toLocaleString('es-ES', {
                              hour: '2-digit',
                              minute: '2-digit',
                              day: '2-digit',
                              month: '2-digit'
                            })}
                          </Text>
                        </div>
                      </div>
                      
                      <div className="event-content">
                        <div className="event-action">
                          <Tag 
                            color={stage.color}
                            className="action-tag"
                          >
                            {formatearAccion(registro.accion)}
                          </Tag>
                          {/* Opción de ubicación del conductor al iniciar/finalizar (igual que en la lista) */}
                          {registro.etiqueta_ubicacion && (
                            <Tag color="#faad14" icon={<ExclamationCircleOutlined />} style={{ marginLeft: 4 }}>
                              {registro.etiqueta_ubicacion}
                            </Tag>
                          )}
                        </div>
                        
                        <Text className="event-description" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                          {textoBaseHistorial(registro)}
                        </Text>
                        {registro.descripcion_conductor && (
                          <div
                            style={{
                              marginTop: 6,
                              padding: "6px 10px",
                              background: "#fffbe6",
                              border: "1px solid #ffe58f",
                              borderLeft: "4px solid #faad14",
                              borderRadius: 6,
                              whiteSpace: "pre-wrap",
                              overflowWrap: "anywhere",
                            }}
                          >
                            <Text strong>Descripción del conductor: </Text>
                            <Text>{registro.descripcion_conductor}</Text>
                          </div>
                        )}
                      </div>
                      
                      {isCritical && (
                        <div className="event-alert">
                          <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginRight: '8px' }} />
                          <Text style={{ color: '#ff4d4f', fontWeight: 500 }}>
                            {registro.accion.toLowerCase().includes('fallido') ? 'Traslado Fallido' : 
                             registro.accion.toLowerCase().includes('cancelado') || registro.accion.toLowerCase().includes('canceló') ? 'Traslado Cancelado' :
                             registro.accion.toLowerCase().includes('desconfirmó') || registro.accion.toLowerCase().includes('desvalidó') ? 'Traslado Desconfirmado' : 'Evento Crítico'}
                          </Text>
                        </div>
                      )}
                    </Card>
                  </div>
                );
              }) : null}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

// Validación de las props con PropTypes
HistorialTrasladoDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  trasladoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default HistorialTrasladoDialog;